/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import useEvent from 'react-use-event-hook';
import { DataType, addClass, hasClass } from '@grapecity/wijmo';
import { CellType, DataMap, CellRangeEventArgs } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import { Code } from 'models/common/CommonCode';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { useLoading } from 'components/process/Loading';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import CustomGrid from 'components/grids/CustomGrid';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { CommonUtil } from 'utils/CommonUtil';
import { hasRole } from 'utils/SessionUtil';
import { findMdModelHeaders, findMdModelBySpec, saveMdModel } from 'apis/md/MdModel';
import { MdModelCondition, MdModelHeader } from 'models/md/MdModel';
import { CrudCode } from 'models/common/Edit';
import ModalSummaryPopup from '../popup/ModelSummaryPopup';

/**
 * @desc 투자비관리 > 기준정보 > 모델 등록/조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.13   jshwang12       신규 생성
 */
const MdMdlInqPage = () => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { isLoading, openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<MdModelCondition>();
  const [rowData, setRowData] = useState<any>([]);
  const [headers, setHeaders] = useState<MdModelHeader[]>([]);
  const [isOpenModelSummaryPopup, setOpenModelSummaryPopup] = useState<boolean>(false);
  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const dragIndex = useRef<any>();

  useEffect(() => {
    getCommonCodes();
    getGridHeaders();
    handleSearch();
  }, []);

  useEffect(() => {
    if (hasRole('MD_IVES_MANAGER') || hasRole('ADM')) {
      setHasAuth(true);
    }
  }, []);

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * 조회
   */
  const handleSearch = () => {
    findMdModelBySpec(condition).then((res) => {
      setRowData(res);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const validate = () => {
    const seen = new Map<string, number>();
    const dupMdlNms = [] as string[];
    (rowData || []).forEach((item) => {
      const mdlNm = item['MDL_NM'];
      if (mdlNm) {
        const count = seen.get(mdlNm) || 0;
        seen.set(mdlNm, count + 1);
        if (count === 1) {
          dupMdlNms.push(mdlNm);
        }
      }
    });

    const valid = (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .map((o, index) => {
        if (_.isEmpty(o['MDL_NM'])) {
          return `${index + 1} : ${t(
            'md.label.모델명을 입력해 주세요.',
            '모델명을 입력해 주세요.'
          )}`;
        }
        if (dupMdlNms.includes(o['MDL_NM'])) {
          return `${index + 1} : ${t(
            'md.label.중복된 모델명이 있습니다.',
            '중복된 모델명이 있습니다.'
          )} (${o['MDL_NM']})`;
        }
      })
      .filter((element) => element !== undefined);

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }

    return true;
  };

  /**
   * 저장
   */
  const handleSave = () => {
    const rows = rowData || [];
    if (rows.length) {
      if (validate()) {
        openCommonModal({
          modalType: 'confirm',
          content: t('com.button.저장하시겠습니까?', '저장하시겠습니까?'),
          yesCallback: async () => {
            openLoading(true);
            saveMdModel(rows)
              .then((res) => {
                openMessageBar({
                  type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
                  content:
                    res?.successOrNot === 'Y'
                      ? t('com.label.저장되었습니다.', '저장되었습니다.')
                      : res.data && typeof res.data === 'string'
                      ? res.data
                      : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
                });
                if (res?.successOrNot === 'Y') {
                  handleSearch();
                }
              })
              .finally(() => openLoading(false));
          },
        });
      }
    }
  };

  /**
   * 행추가
   */
  const handleAddRow = () => {
    const newRow = {
      crudKey: CrudCode.CREATE,
    };
    setRowData([newRow, ...rowData]);
  };

  /**
   * 행상제
   */
  const handleDelRow = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    if (!selectedRows) return;

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('md.label.모델 목록', '모델 목록'),
      t('md.label.모델 목록', '모델 목록')
    );
  };

  /**
   * 그리드 헤더 동적으로 그리기 위해 목록 조회
   */
  const getGridHeaders = () => {
    findMdModelHeaders().then((result) => {
      setHeaders(result);
    });
  };

  const mapDisplayStacking = useMemo(() => {
    return Object.fromEntries((code?.mdStackingTp || []).map((o) => [o.cmnCd, o.optValCtn1]));
  }, [code?.mdStackingTp]);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('MD_CELL_TP'), // Cell Type
      getCommonCodeNames('MD_CPTN_TP_CD'), // 셀 조성
      getCommonCodeNames('MD_DIR_TP'), // 셀 양/단방향
      getCommonCodeNames('MD_SIDE_TP'), // PKG 3면/4면
      getCommonCodeNames('MD_FOLD_TP'), // Folding Type
      getCommonCodeNames('MD_STACKING_TP'), // Stacking Type
      getCommonCodeNamesCondition({
        cmnGrCd: 'MDL_SPEC_CD',
        optValCtn7: 'Y', // 모델 속성여부
      }),
    ]).then(
      async ([mdCellTp, mdCptnTpCd, mdDirTp, mdSideTp, mdFoldTp, mdStackingTp, mdlSpecCd]) => {
        // 사양코드 값 그룹코드 목록
        const colGrCds = (mdlSpecCd || []).reduce((acc: string[], cur) => {
          if (cur.optValCtn4 && !acc.includes(cur.optValCtn4)) {
            acc.push(cur.optValCtn4);
          }
          return acc;
        }, []);
        // 사양코드 값 그룹코드 값 조회
        const colGrCdsResults = await Promise.all(
          colGrCds.map((cmnGrCd) => getCommonCodeNames(cmnGrCd))
        );
        const mapColGrCds = colGrCds.reduce((acc, cmnGrCd, index) => {
          acc[cmnGrCd] = colGrCdsResults[index];
          return acc;
        }, {} as Record<string, Code[]>);

        setCode({
          mdCellTp: mdCellTp,
          mdCptnTpCd: mdCptnTpCd,
          mdDirTp: mdDirTp,
          mdSideTp: mdSideTp,
          mdFoldTp: mdFoldTp,
          mdStackingTp: mdStackingTp,
          colGrCds: mapColGrCds, // 그리드 셀 콤보박스에서 사용되는 공통코드 (ex. {MD_CELL_TP: [] as Code[], ...})
        });
      }
    );
  };

  const layoutDefinition = useMemo(() => {
    const STACK_TYPE_SPEC_CD = 'S00001'; // Stacking Type 사양코드
    const STACK_CNT_SPEC_CD = 'S00045'; // 완성 Cell > Stack 수 사양코드
    const specLayouts = (headers || []).reduce((acc, item) => {
      const columnDef = {
        binding: item.specCd,
        header: item.specNm,
        width: '*',
        minWidth: 90,
        ...(item.specValTp === 'LIST' &&
          item.specValCd && {
            cssClass: 'WijmoSelect',
            dataMap: new DataMap(code?.colGrCds?.[item.specValCd] || [], 'cmnCd', 'cmnCdNm'),
          }),
        ...(item.specValTp === 'NUM' && {
          align: 'right',
          dataType: DataType.Number,
        }),
      };
      // ctgrNm이 있는 경우 그룹핑
      if (item.ctgrNm) {
        let group = acc.find((g) => g.header === item.ctgrNm);
        if (!group) {
          // 기존 그룹없는 경우 생성
          group = {
            header: item.ctgrNm,
            columns: [],
          };
          acc.push(group);
        }
        group.columns.push(columnDef); // 그룹에 컬럼 추가
        if (item.specCd === STACK_CNT_SPEC_CD) {
          // StackingType이 있는 경우 완성 Cell > Stack 수 다음 칸에 Stack +1 표시용 컬럼 추가
          const stackType = (headers || []).filter((o) => o.specCd === STACK_TYPE_SPEC_CD);
          if (stackType.length > 0) {
            group.columns.push({
              binding: 'stackDisplay',
              header: item.specNm,
              width: 40,
              minWidth: 40,
              isReadOnly: true,
              // Stack Type 값에 따라 MD_STACKING_TP > 옵션1값 을 표시
              cellTemplate: (params) => {
                return mapDisplayStacking?.[params.item[STACK_TYPE_SPEC_CD] || ''] || '';
              },
            });
          }
        }
      } else {
        // ctgrNm이 없는 경우 최상위에 추가
        acc.push(columnDef);
      }

      return acc;
    }, [] as any[]);

    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'sortable',
        header: String(t('com.label.순서', '순서')),
        minWidth: 43,
        width: 43,
        visible: hasAuth,
        align: 'center',
        isReadOnly: true,
        cssClass: 'WijmoCellMove',
        cellTemplate: '<Button />',
      },
      {
        binding: 'MDL_NM',
        header: String(t('com.label.모델명', '모델명')),
        width: 150,
        minWidth: 80,
        align: 'left',
      },
      /*{
        binding: 'SPEC_SEQ',
        header: String(t('com.label.Sort Ord', 'Sort Ord')),
        width: 80,
        minWidth: 50,
        align: 'center',
        dataType: DataType.Number,
        format: 'n0',
      },*/
      ...(specLayouts || []), // 사양정보 기준 그리드 헤더 추가 (MDL_SPEC_CD.OPT_VAL_CTN6 = 'Y')
      {
        binding: 'DATA_UPD_USER_NM',
        header: String(t('com.label.수정자', '수정자')),
        width: 130,
        minWidth: 80,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'DATA_UPD_DTM',
        header: String(t('com.label.수정일시', '수정일시')),
        width: 130,
        minWidth: 80,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'MDL_ID',
        visible: false,
      },
    ];
  }, [code, headers]);

  const onBeginningEdit = useEvent((grid, e) => {
    if (!hasAuth) {
      e.cancel = true;
    } else {
      const binding = grid.columns[e.col].binding;
      const item = grid.rows[e.row].dataItem;

      if (CrudCode.CREATE !== item.crudKey) {
        // 수정 시 모델명 수정불가
        if ('MDL_NM' === binding) {
          e.cancel = true;
        }
      }

      // Sort Ord
      /*if (binding === 'SPEC_SEQ') {
        const cell = grid.cells.getCellElement(e.row, e.col);
        // editor(input) 생성될 때까지 잠깐 기다림
        setTimeout(() => {
          // 셀 안쪽에서 input 요소 찾기
          const input = cell.querySelector('input');
          if (input) {
            // 숫자만 입력되도록 필터링
            input.addEventListener('input', () => {
              input.value = input.value.replace(/[^0-9]/g, '');
            });
          }
        });
      }*/
    }
  });

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (!hasAuth) {
        cell.ariaReadOnly = true;
      } else {
        if (CrudCode.CREATE !== item.crudKey) {
          // 수정 시 모델명 수정불가
          if ('MDL_NM' === binding) {
            cell.ariaReadOnly = true;
          }
        }
      }
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.formatItem.addHandler((s, e) => {
      const item = e.panel.rows[e.row]?.dataItem;
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          // 이미 사용중인 항목인 경우 선택불가/readonly
          if (item?.['USED_MDL_YN'] === 'Y') {
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
      const binding = s.columns[e.col].binding;
      if (s.cells === e.panel) {
        if ('sortable' === binding) {
          e.cell.draggable = true;
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (row?.dataItem?.['USED_MDL_YN'] === 'Y') {
              row.isSelected = false;
            }
          });
        });
      }
    });
    grid.hostElement.addEventListener('dragstart', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggingRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
    grid.hostElement.addEventListener('dragover', (e) => {
      const hti = grid.hitTest(e);
      if (hti.panel === grid.cells) {
        e.preventDefault();
      }
    });
    grid.hostElement.addEventListener('drop', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggedRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('md.label.모델명', '모델명'))}</label>
                <CustomInputWithSearch
                  name="mdlNm"
                  value={condition?.mdlNm}
                  placeholder={String(
                    t('md.label.모델명을 입력해 주세요.', '모델명을 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange('mdlNm', e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.셀 Type', '셀 Type'))}</label>
                <MultiComboBox
                  id="cellType"
                  options={code?.mdCellTp || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.cellTypes || []}
                  onChange={(value) => handleOnChange('cellTypes', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.완성 Cell 조성', '완성 Cell 조성'))}</label>
                <MultiComboBox
                  id="finishedCellComposition"
                  options={code?.mdCptnTpCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.finishedCellCompositions || []}
                  onChange={(value) => handleOnChange('finishedCellCompositions', value)}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{String(t('md.label.완성 셀 방향', '완성 셀 방향'))}</label>
                <MultiComboBox
                  id="finishedCellDirectionType"
                  options={code?.mdDirTp || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.finishedCellDirectionTypes || []}
                  onChange={(value) => handleOnChange('finishedCellDirectionTypes', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.PKG 면', 'PKG 면'))}</label>
                <MultiComboBox
                  id="pkgSealingSideType"
                  options={code?.mdSideTp || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.pkgSealingSideTypes || []}
                  onChange={(value) => handleOnChange('pkgSealingSideTypes', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.Folding Type', 'Folding Type'))}</label>
                <MultiComboBox
                  id="eolFoldingType"
                  options={code?.mdFoldTp || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.eolFoldingTypes || []}
                  onChange={(value) => handleOnChange('eolFoldingTypes', value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons setReset={setCondition} onSearch={() => handleSearch()} />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.모델 목록', '모델 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="docu"
            onClick={() => setOpenModelSummaryPopup(true)}
            disableRipple
          >
            {t('md.label.전체 요약', '전체 요약')}
          </Button>
          {hasAuth && (
            <>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.button.행추가', '행추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={500}
        isReadOnly={!hasAuth}
        allowPinning={false}
        allowSorting={false}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
        excludeFilter={['sortable']}
        draggingRow={(grid, e) => {
          dragIndex.current = e.row;
          grid.collectionView.moveCurrentToPosition(e.row);
        }}
        draggedRow={(grid, e) => {
          const dropIndex = e.row;
          const arr = grid.collectionView.sourceCollection;
          grid.collectionView.deferUpdate(() => {
            const item = arr[dragIndex.current];
            arr.splice(dragIndex.current, 1);
            arr.splice(dropIndex, 0, item);
            grid.collectionView.moveCurrentToPosition(dropIndex);
            if (item.crudKey !== CrudCode.CREATE) {
              item.crudKey = CrudCode.UPDATE;
            }
          });
        }}
      />
      {hasAuth && (
        <GlobalBtnGroup>
          <Button
            css={IconButton.button}
            className="save"
            onClick={handleSave}
            disabled={isLoading}
            disableRipple
          >
            {t('com.button.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}

      {isOpenModelSummaryPopup && (
        <ModalSummaryPopup
          open={isOpenModelSummaryPopup}
          close={() => setOpenModelSummaryPopup(false)}
        />
      )}
    </>
  );
};

export default MdMdlInqPage;
